/**
 * WHAT THIS PRODUCT IS, TODAY
 * ===========================================================================
 * This codebase started as a fitness tracker and is now Along, a behavioral-
 * activation companion for therapy practices. The fitness surfaces —
 * workouts, macros, meal plans, personal records, run programmes — are not
 * deleted. They are switched off here, in one place.
 *
 * WHY OFF RATHER THAN DELETED
 * Three reasons, in order of how much they cost to get wrong:
 *
 *   1. THE ACTIVATION ENGINE STILL PRESCRIBES MOVEMENT. Movement has the
 *      strongest evidence base of any activation, and the session machinery —
 *      timers, exercise data, the workout modal — is the thing a patient at
 *      capacity actually opens. Deleting it would mean rebuilding it the first
 *      time a clinician asks for a structured option.
 *   2. THE PIVOT IS A HYPOTHESIS. It is supported by an RCT and a market gap,
 *      not by a signed pilot. Ten therapist conversations could send this back
 *      toward consumer, and a codebase that burned its own surfaces cannot go
 *      back cheaply.
 *   3. THEY ARE TESTED AND WORKING. Deleting working, tested code to express a
 *      change of direction is theatre.
 *
 * A flag is only honest if there is exactly one of it. Anything that asks
 * "are we still a fitness app?" asks it here — no second copy in a component,
 * no `if (isProof)` sprinkled through JSX.
 */

/**
 * Surfaces the patient app offers.
 *
 * `nutrition` and `train` are the archived fitness destinations. They remain
 * routable — an existing user's data is still theirs and still reachable — but
 * they hold no permanent chrome, which is the actual statement a product makes
 * about what it is about.
 */
export const PATIENT_TABS = ["home", "train", "profile", "nutrition"];

/**
 * Nothing is archived. The product is a training app whose spine is the mood
 * loop — check in, train, check out, see what it did — so training gets its
 * permanent slot back. Run stays folded into Move, where it already lived.
 */
export const ARCHIVED_TABS = ["run"];

/**
 * Whether a destination gets a permanent slot in the tab bar.
 *
 * The tab bar is the most persistent statement the product makes about what it
 * is for. A therapy tool with a Food tab in permanent chrome is a fitness app
 * with a mood widget, whatever the marketing says.
 */
export function isPrimaryTab(id) {
  return PATIENT_TABS.includes(id);
}

/** Whether a surface is archived — reachable, but not advertised. */
export function isArchived(id) {
  return ARCHIVED_TABS.includes(id);
}

/**
 * Which app a signed-in person gets.
 *
 * Deliberately not a permission system. A clinician seeing the caseload and a
 * patient seeing the ritual is a routing decision; who is allowed to read whose
 * check-ins is a Postgres row-level-security decision, and it lives in the
 * database where it cannot be bypassed by editing the client bundle. Anything
 * that treats this function as a security boundary is a bug.
 */
export function appFor(profile) {
  return profile?.role === "clinician" ? "clinician" : "patient";
}
